import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { dashboardApi } from '../../services/api';
import { RootState } from '../index';
import { 
  SuperAdminDashboard, 
  OrgAdminDashboard, 
  IDEStatsResponse, 
  WebStatsResponse,
  UserStats,
  OrganizationStats
} from '../../types/dashboard';

interface DashboardState {
  superAdminData: SuperAdminDashboard | null;
  orgAdminData: OrgAdminDashboard | null;
  ideStats: IDEStatsResponse | null;
  webStats: WebStatsResponse | null;
  userStats: UserStats | null;
  orgStats: OrganizationStats | null;
  isLoading: boolean;
  isIdeStatsLoading: boolean;
  isWebStatsLoading: boolean;
  error: string | null;
  ideError: string | null;
  webError: string | null;
  lastUpdated: string | null;
}

const initialState: DashboardState = {
  superAdminData: null,
  orgAdminData: null,
  ideStats: null,
  webStats: null,
  userStats: null,
  orgStats: null,
  isLoading: false,
  isIdeStatsLoading: false,
  isWebStatsLoading: false,
  error: null,
  ideError: null,
  webError: null,
  lastUpdated: null,
};

export const fetchIDEStats = createAsyncThunk(
  'dashboard/fetchIDEStats',
  async (params: { start_date?: string; end_date?: string; org_id?: string }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const response = state.auth.user?.role === 'super_admin'
        ? await dashboardApi.getSuperAdminIDEStats(params)
        : await dashboardApi.getOrgAdminIDEStats(params);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.detail) {
        return rejectWithValue(error.response.data.detail);
      }
      return rejectWithValue('Failed to fetch IDE stats');
    }
  }
);

export const fetchWebStats = createAsyncThunk(
  'dashboard/fetchWebStats',
  async (params: { start_date?: string; end_date?: string; org_id?: string }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const response = state.auth.user?.role === 'super_admin'
        ? await dashboardApi.getSuperAdminWebStats(params)
        : await dashboardApi.getOrgAdminWebStats(params);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.detail) {
        return rejectWithValue(error.response.data.detail);
      }
      return rejectWithValue('Failed to fetch web stats');
    }
  }
);

export const fetchUserStats = createAsyncThunk(
  'dashboard/fetchUserStats',
  async (days: number = 30, { rejectWithValue }) => {
    try {
      const response = await dashboardApi.getUserStats(days);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.detail) {
        return rejectWithValue(error.response.data.detail);
      }
      return rejectWithValue('Failed to fetch user stats');
    }
  }
);

export const fetchOrgStats = createAsyncThunk(
  'dashboard/fetchOrgStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await dashboardApi.getOrgStats();
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.detail) {
        return rejectWithValue(error.response.data.detail);
      }
      return rejectWithValue('Failed to fetch organization stats');
    }
  }
);

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearDashboard: (state) => {
      state.superAdminData = null;
      state.orgAdminData = null;
      state.ideStats = null;
      state.webStats = null;
      state.userStats = null;
      state.orgStats = null;
      state.error = null;
      state.ideError = null;
      state.webError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // IDE Stats
      .addCase(fetchIDEStats.pending, (state) => {
        state.isIdeStatsLoading = true;
        state.ideError = null;
      })
      .addCase(fetchIDEStats.fulfilled, (state, action) => {
        state.isIdeStatsLoading = false;
        state.ideStats = action.payload;
        state.ideError = null;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchIDEStats.rejected, (state, action) => {
        state.isIdeStatsLoading = false;
        state.ideError = action.payload as string;
      })
      // Web Stats
      .addCase(fetchWebStats.pending, (state) => {
        state.isWebStatsLoading = true;
        state.webError = null;
      })
      .addCase(fetchWebStats.fulfilled, (state, action) => {
        state.isWebStatsLoading = false;
        state.webStats = action.payload;
        state.webError = null;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchWebStats.rejected, (state, action) => {
        state.isWebStatsLoading = false;
        state.webError = action.payload as string;
      })
      // User Stats
      .addCase(fetchUserStats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserStats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.userStats = action.payload;
        state.error = null;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchUserStats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Org Stats
      .addCase(fetchOrgStats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOrgStats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.orgStats = action.payload;
        state.error = null;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchOrgStats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearDashboard } = dashboardSlice.actions;
export default dashboardSlice.reducer;
